import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  ArrowLeft, 
  Copy, 
  ExternalLink, 
  Mail, 
  DollarSign,
  User,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { paymentAPI } from '../../services/api';
import { formatCurrency, formatDateTime, getTimeUntilExpiration } from '../../utils/formatters';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import StatusBadge from '../common/StatusBadge';
import Loader from '../common/Loader';

const PaymentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useAdminTheme();
  const [payment, setPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);

  const fetchPaymentDetails = useCallback(async () => {
    try {
      const response = await paymentAPI.getPaymentRequest(id);
      if (response.success) {
        setPayment(response.data);
        if (response.data.status === 'pending') {
          const timeUntilExpiration = getTimeUntilExpiration(response.data.expiresAt);
          setTimeLeft(timeUntilExpiration);
        }
      } else {
        toast.error('Payment request not found');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Failed to load payment details');
      navigate('/admin/dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]);

  useEffect(() => {
    let interval;
    if (payment && payment.status === 'pending') {
      interval = setInterval(() => {
        const timeUntilExpiration = getTimeUntilExpiration(payment.expiresAt);
        setTimeLeft(timeUntilExpiration);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [payment]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const handleBackNavigation = () => {
    // Check if there's a previous page in history
    if (location.state?.from) {
      navigate(location.state.from);
    } else if (window.history.length > 1) {
      // Go back to previous page in browser history
      navigate(-1);
    } else {
      // Fallback: determine dashboard based on current path
      if (location.pathname.includes('/admin/')) {
        navigate('/admin/dashboard');
      } else if (location.pathname.includes('/user/')) {
        navigate('/user/dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const getEffectiveStatus = () => {
    if (payment.status === 'pending' && timeLeft?.expired) {
      return 'expired';
    }
    return payment.status;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader size="lg" text="Loading payment details..." />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className={`text-center py-12 transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-page-dark' : 'bg-gray-50'
      }`}>
        <h2 className={`text-2xl font-bold transition-colors duration-300 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>Payment not found</h2>
        <p className={`mt-2 transition-colors duration-300 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>The payment request you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 btn btn-primary"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const effectiveStatus = getEffectiveStatus();

  return (
    <div className={`max-w-8xl mx-auto px-4 sm:px-5 lg:px-6 py-6 transition-colors duration-300 ${
      isDarkMode ? 'bg-skyone-page-dark' : 'bg-gray-50'
    }`}>
      <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackNavigation}
            className={`p-2 transition-colors duration-200 ${
              isDarkMode 
                ? 'text-gray-400 hover:text-gray-200' 
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className={`text-2xl font-bold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>Payment Details</h1>
            <p className={`transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>Payment request #{payment._id.slice(-8)}</p>
          </div>
        </div>
        <StatusBadge status={effectiveStatus} showIcon={true} />
      </div>

      {/* Time Remaining Alert for Pending Payments */}
      {effectiveStatus === 'pending' && timeLeft && !timeLeft.expired && (
        <div className={`border rounded-lg p-4 transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-yellow-900/20 border-yellow-700' 
            : 'bg-warning-50 border-warning-200'
        }`}>
          <div className="flex items-center">
            <Clock className={`w-5 h-5 mr-3 transition-colors duration-300 ${
              isDarkMode ? 'text-yellow-400' : 'text-warning-600'
            }`} />
            <div>
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-yellow-200' : 'text-warning-800'
              }`}>
                Payment expires in {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
              </h3>
              <p className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? 'text-yellow-300' : 'text-warning-700'
              }`}>
                This payment link will expire on {formatDateTime(payment.expiresAt)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Expired Alert */}
      {effectiveStatus === 'expired' && (
        <div className={`border rounded-lg p-4 transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-red-900/20 border-red-700' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center">
            <AlertCircle className={`w-5 h-5 mr-3 transition-colors duration-300 ${
              isDarkMode ? 'text-red-400' : 'text-red-600'
            }`} />
            <div>
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-red-200' : 'text-red-800'
              }`}>Payment Expired</h3>
              <p className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? 'text-red-300' : 'text-red-700'
              }`}>
                This payment link expired on {formatDateTime(payment.expiresAt)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Information */}
          <div className={`card transition-colors duration-300 ${
            isDarkMode ? 'card-dark' : 'card'
          }`}>
            <div className={`card-header transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
            }`}>
              <h2 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Payment Information</h2>
            </div>
            <div className={`card-body space-y-4 transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
            }`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>Amount</label>
                  <div className="flex items-center space-x-2">
                    <DollarSign className={`w-5 h-5 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <span className={`text-2xl font-bold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {formatCurrency(payment.amount, payment.currency)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>Currency</label>
                  <span className={`text-lg font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {payment.currency}
                  </span>
                </div>
              </div>

              <div>
                <label className={`label transition-colors duration-300 ${
                  isDarkMode ? 'label-dark' : 'label'
                }`}>Description</label>
                <div className="flex items-start space-x-2">
                  <FileText className={`w-5 h-5 mt-0.5 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                  <p className={`transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{payment.description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className={`card transition-colors duration-300 ${
            isDarkMode ? 'card-dark' : 'card'
          }`}>
            <div className={`card-header transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
            }`}>
              <h2 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Client Information</h2>
            </div>
            <div className={`card-body space-y-4 transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
            }`}>
              <div>
                <label className={`label transition-colors duration-300 ${
                  isDarkMode ? 'label-dark' : 'label'
                }`}>Client Name</label>
                <div className="flex items-center space-x-2">
                  <User className={`w-5 h-5 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                  <span className={`text-lg font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {payment.clientName}
                  </span>
                </div>
              </div>
              <div>
                <label className={`label transition-colors duration-300 ${
                  isDarkMode ? 'label-dark' : 'label'
                }`}>Email Address</label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Mail className={`w-5 h-5 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <span className={`transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{payment.clientEmail}</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(payment.clientEmail, 'Email address')}
                    className={`btn btn-sm transition-colors duration-300 ${
                      isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'
                    }`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Link */}
          {payment.paymentLink && effectiveStatus === 'pending' && (
            <div className={`card transition-colors duration-300 ${
              isDarkMode ? 'card-dark' : 'card'
            }`}>
              <div className={`card-header transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
              }`}>
                <h2 className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Payment Link</h2>
              </div>
              <div className={`card-body transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
              }`}>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={payment.paymentLink}
                    readOnly
                    className={`input flex-1 transition-colors duration-300 ${
                      isDarkMode ? 'input-dark' : 'input'
                    }`}
                  />
                  <button
                    onClick={() => copyToClipboard(payment.paymentLink, 'Payment link')}
                    className={`btn transition-colors duration-300 ${
                      isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'
                    }`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <a
                    href={payment.paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
                <p className={`text-sm mt-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Share this link with your client to complete the payment
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className={`card transition-colors duration-300 ${
            isDarkMode ? 'card-dark' : 'card'
          }`}>
            <div className={`card-header transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Status</h3>
            </div>
            <div className={`card-body transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
            }`}>
              <div className="text-center">
                <StatusBadge status={effectiveStatus} showIcon={true} />
                {effectiveStatus === 'paid' && (
                  <div className={`mt-4 flex items-center justify-center transition-colors duration-300 ${
                    isDarkMode ? 'text-green-400' : 'text-green-600'
                  }`}>
                    <CheckCircle className="w-6 h-6 mr-2" />
                    <span className="text-sm font-medium">Payment Completed</span>
                  </div>
                )}
                {effectiveStatus === 'failed' && (
                  <div className={`mt-4 flex items-center justify-center transition-colors duration-300 ${
                    isDarkMode ? 'text-red-400' : 'text-red-600'
                  }`}>
                    <XCircle className="w-6 h-6 mr-2" />
                    <span className="text-sm font-medium">Payment Failed</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className={`card transition-colors duration-300 ${
            isDarkMode ? 'card-dark' : 'card'
          }`}>
            <div className={`card-header transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Timeline</h3>
            </div>
            <div className={`card-body space-y-4 transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
            }`}>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className={`w-2 h-2 rounded-full mt-2 transition-colors duration-300 ${
                    isDarkMode ? 'bg-blue-400' : 'bg-primary-600'
                  }`}></div>
                </div>
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Payment Request Created</p>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>{formatDateTime(payment.createdAt)}</p>
                </div>
              </div>

              {payment.paidAt && (
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className={`w-2 h-2 rounded-full mt-2 transition-colors duration-300 ${
                      isDarkMode ? 'bg-green-400' : 'bg-green-600'
                    }`}></div>
                  </div>
                  <div>
                    <p className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>Payment Completed</p>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>{formatDateTime(payment.paidAt)}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className={`w-2 h-2 rounded-full mt-2 transition-colors duration-300 ${
                    isDarkMode ? 'bg-gray-500' : 'bg-gray-400'
                  }`}></div>
                </div>
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Expires</p>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>{formatDateTime(payment.expiresAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stripe Information */}
          {payment.stripeSessionId && (
            <div className={`card transition-colors duration-300 ${
              isDarkMode ? 'card-dark' : 'card'
            }`}>
              <div className={`card-header transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
              }`}>
                <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Stripe Information</h3>
              </div>
              <div className={`card-body space-y-3 transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
              }`}>
                <div>
                  <label className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>Session ID</label>
                  <div className="flex items-center space-x-2">
                    <code className={`text-sm px-2 py-1 rounded flex-1 min-w-0 break-all transition-colors duration-300 ${
                      isDarkMode ? 'bg-skyone-inset-dark text-zinc-300' : 'bg-gray-100 text-gray-900'
                    }`}>
                      {payment.stripeSessionId}
                    </code>
                    <button
                      onClick={() => copyToClipboard(payment.stripeSessionId, 'Session ID')}
                      className={`btn btn-sm flex-shrink-0 transition-colors duration-300 ${
                        isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'
                      }`}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {payment.stripePaymentIntentId && (
                  <div>
                    <label className={`label transition-colors duration-300 ${
                      isDarkMode ? 'label-dark' : 'label'
                    }`}>Payment Intent ID</label>
                    <div className="flex items-center space-x-2">
                      <code className={`text-sm px-2 py-1 rounded flex-1 min-w-0 break-all transition-colors duration-300 ${
                        isDarkMode ? 'bg-skyone-inset-dark text-zinc-300' : 'bg-gray-100 text-gray-900'
                      }`}>
                        {payment.stripePaymentIntentId}
                      </code>
                      <button
                        onClick={() => copyToClipboard(payment.stripePaymentIntentId, 'Payment Intent ID')}
                        className={`btn btn-sm flex-shrink-0 transition-colors duration-300 ${
                          isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'
                        }`}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="pt-2">
                  <a
                    href={`https://dashboard.stripe.com/test/payments${payment.stripePaymentIntentId ? `/${payment.stripePaymentIntentId}` : ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`btn w-full transition-colors duration-300 ${
                      isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'
                    }`}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View in Stripe Dashboard
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default PaymentDetails;
